import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useShallow } from 'zustand/react/shallow'
import { useCrmSettingsStore } from '../stores/crmSettingsStore'
import { usePageTour } from '../hooks/usePageTour'
import { CrmTopbarPortal } from '../CrmLayout'
import { StagesEditor } from './StagesEditor'
import { DataImport } from './DataImport/DataImport'
import './SettingsPage.scss'

const SECTIONS = [
    ['renewal', 'Renewal Automation'],
    ['branding', 'Branding'],
    ['team', 'Team & Roles'],
    ['stages', 'Pipeline Stages'],
    ['sources', 'Lead Sources'],
    ['import', 'Data Import'],
]

// The Settings tour (see usePageTour). Every step but the first is on Pipeline
// Stages, where the page always opens; a replay from another section skips them.
const SETTINGS_TOUR_VERSION = 1
const SETTINGS_TOUR_STEPS = [
    {
        element: '.settings-page .settings-nav',
        data: { id: 'sections' },
        popover: {
            title: 'Settings sections',
            description: 'Choose a category here. These settings apply to your whole team.',
        },
    },
    {
        element: '.settings-page .stage-list',
        data: { id: 'stages' },
        popover: {
            title: 'Your pipeline stages',
            description: 'These are the columns on your Pipeline board. Click a name to rename it, and use the arrows to reorder or the bin to remove. Changes save right away.',
        },
    },
    {
        element: '.settings-page .stage-add',
        data: { id: 'add-stage' },
        popover: {
            title: 'Add a stage',
            description: 'Type a name and select Add stage to add a new column to the end of your pipeline.',
        },
    },
    {
        // In the tab bar on desktop, the section strip on phones.
        element: '.crm-tour-btn',
        data: { id: 'replay' },
        popover: {
            title: 'Replay any time',
            description: 'You can start this tutorial again anytime from here.',
        },
    },
]

const WINDOWS = [[0, 'At expiration'], [1, '1 month'], [2, '2 months'], [3, '3 months'], [6, '6 months']]
const COLORS = ['#4A90E2', '#0f9d8f', '#7048cc', '#2e9967', '#d07020', '#c93535']

const Toggle = ({ on, onChange }) => (
    <div className={`switch${on ? '' : ' off'}`} onClick={() => onChange(!on)}><div className="knob" /></div>
)

const ToggleRow = ({ name, sub, on, onChange }) => (
    <div className="toggle-row">
        <div className="toggle-text">
            <div className="toggle-name">{name}</div>
            <div className="toggle-sub">{sub}</div>
        </div>
        <Toggle on={on} onChange={onChange} />
    </div>
)

export const SettingsPage = () => {
    const [section, setSection] = useState('stages')
    const { settings, sources, accountUsers, loading, fetchAll, updateSetting } = useCrmSettingsStore(
        useShallow((s) => ({
            settings: s.settings,
            sources: s.sources,
            accountUsers: s.accountUsers,
            loading: s.loading,
            fetchAll: s.fetchAll,
            updateSetting: s.updateSetting,
        }))
    )

    useEffect(() => {
        fetchAll()
    }, [])

    // The first-visit tour, once settings have loaded. Read live — a warm
    // store's value at mount predates the fetch the effect above has just started.
    const tour = usePageTour({ key: 'settings', version: SETTINGS_TOUR_VERSION, steps: SETTINGS_TOUR_STEPS })
    useEffect(function autoStartTour() {
        const st = useCrmSettingsStore.getState()
        if (st.loading || !st.settings) return
        tour.autoStart()
    }, [loading])

    const s = settings || {}

    const renderContent = () => {
        if (loading && !settings) return <div className="field-help">Loading…</div>
        switch (section) {
            case 'renewal':
                return (
                    <div>
                        <div className="field-label">Renewal window</div>
                        <div className="field-help">How far ahead of a lease’s end we start treating a client as a renewal opportunity.</div>
                        <div className="seg">
                            {WINDOWS.map(([v, l]) => (
                                <div key={v} className={`seg-opt${s.renewal_window_months === v ? ' active' : ''}`} onClick={() => updateSetting({ renewal_window_months: v })}>{l}</div>
                            ))}
                        </div>
                        <div style={{ marginTop: 18 }}>
                            <ToggleRow name="Auto-drop onto the pipeline" sub="Renewals appear in the Lead column automatically when a client enters the window." on={!!s.auto_surface} onChange={(v) => updateSetting({ auto_surface: v })} />
                            <ToggleRow name="Assign renewal to the original salesperson" sub="The rep who sold the last car gets the renewal. Off = renewals go to a shared pool." on={!!s.assign_to_original_rep} onChange={(v) => updateSetting({ assign_to_original_rep: v })} />
                            <ToggleRow name="Notify rep when a renewal drops" sub="Send a notification the moment a client enters the window." on={!!s.notify_rep} onChange={(v) => updateSetting({ notify_rep: v })} />
                        </div>
                    </div>
                )
            case 'branding':
                return (
                    <div>
                        <div className="field-label">Organization</div>
                        <div className="field-help">Your account name, shown across the CRM.</div>
                        <input className="text-input" value={s.name || ''} readOnly />
                        <div className="field-label" style={{ marginTop: 20 }}>Brand color</div>
                        <div className="field-help">Accent color for the CRM.</div>
                        <div className="swatches">
                            {COLORS.map((c) => (
                                <div key={c} className={`swatch${s.brand_color === c ? ' on' : ''}`} style={{ background: c }} onClick={() => updateSetting({ brand_color: c })} />
                            ))}
                        </div>
                    </div>
                )
            case 'team':
                return (
                    <div>
                        <div className="field-label">Team</div>
                        <div className="field-help">Everyone on this account. An admin can see and change any rep’s contacts, pursuits and reports, and is the only role that can reach this Settings page; a member is limited to their own. Roles are assigned by TrueDesking — contact support to change one.</div>
                        <div className="team-list">
                            {accountUsers.map((u) => (
                                <div className="team-row" key={u.id}>
                                    <div className="team-av">{(u.username || u.email || '?').slice(0, 2).toUpperCase()}</div>
                                    <div className="team-main">
                                        <div className="team-name">{u.username || u.email}</div>
                                        <div className="team-email">{u.email}</div>
                                    </div>
                                    {/* Older payloads predate the role field; absent reads as Member,
                                        which is what the backend defaults those users to. */}
                                    <span className={`team-role${u.role === 'Admin' ? ' admin' : ''}`}>
                                        {u.role === 'Admin' ? 'Admin' : 'Member'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            case 'stages':
                return <StagesEditor />
            case 'import':
                return <DataImport />
            case 'sources':
                return (
                    <div>
                        <div className="field-label">Lead sources</div>
                        <div className="field-help">The channels a lead can come in from. These are fixed options today.</div>
                        <div className="source-list">
                            {sources.map((src) => <span className="source-chip" key={src.value}>{src.label}</span>)}
                        </div>
                    </div>
                )
            default:
                return null
        }
    }

    return (
        <Box className="settings-page">
            <div className="layout">
                <div className="side">
                    {/* The menu alone, not the full-height rail: the tour's first target. */}
                    <div className="settings-nav">
                        <div className="side-title">CRM</div>
                        {/* In the CRM tab bar; leads the section strip on phones. */}
                        <CrmTopbarPortal>
                            <button
                                type="button"
                                className="crm-tour-btn"
                                aria-label="Show Tutorial"
                                title="Show Tutorial"
                                onClick={tour.replay}
                            >
                                <HelpOutlineIcon />
                                <span className="crm-tour-label">Show Tutorial</span>
                            </button>
                        </CrmTopbarPortal>
                        {SECTIONS.map(([k, l]) => (
                            <div key={k} className={`side-item${section === k ? ' active' : ''}`} onClick={() => setSection(k)}>{l}</div>
                        ))}
                    </div>
                </div>
                <div className="content">
                    <div className="panel">
                        <div className="panel-body">{renderContent()}</div>
                    </div>
                </div>
            </div>
        </Box>
    )
}
